// lista kategorii

import React, { FC, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { PlusCircle, DashCircle, ExclamationCircle, Search } from 'react-bootstrap-icons';
import { Grid } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';

import { useGetProductsCategories } from 'api';
import { ICategoryListItem, IProductsCategoriesRequest, IProductsRequest } from 'api/types';
import { Link } from 'components/controls';
import { WiseCategoriesSkeleton } from 'pages/wise/WiseProducts/components/WiseSkeletons/WiseSkeletons';

import styles from 'theme/pages/wise/WiseProducts/components/WiseCategories/WiseCategories.module.scss';

// typ danych wejściowych
interface Props {
  columnsCount?: number;
  chosenCategoryId?: number;
  searchKeyword?: string;
  productsQueryParams: IProductsRequest;
}

const WiseCategories: FC<Props> = ({ columnsCount = 1, chosenCategoryId, productsQueryParams }) => {
  // parametry zapytania do api
  const [categoriesQuery, setCategoriesQuery] = useState<IProductsCategoriesRequest>({
    ...productsQueryParams,
    page: 1,
    limit: 999
  });

  const { t } = useTranslation();

  // lista ID'ków rozwiniętych kategorii
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);

  // fraza wyszukiwarki kategorii
  const [categorySearch, setCategorySearch] = useState('');

  // pobranie listy kategorii
  const { data: categoriesData } = useGetProductsCategories(categoriesQuery, {
    keepPreviousData: true
  });

  // aktualizacja parametrów zapytania do api przy zmianie parametrów zapytaqnia o produkty
  useEffect(() => {
    if (chosenCategoryId) {
      if (!expandedCategoryIds.includes(chosenCategoryId)) {
        setExpandedCategoryIds((prev) => [...prev, chosenCategoryId]);
      }
    }
    setCategoriesQuery({
      ...productsQueryParams,
      page: 1,
      limit: 999
    });
  }, [productsQueryParams]);

  // filtrowanie kategorii po frazie (rekurencyjnie sprawdza nazwę i podkategorie)
  const categoryMatchesSearch = (category: ICategoryListItem, phrase: string): boolean => {
    if (category.name.toLowerCase().includes(phrase.toLowerCase())) return true;
    return category.subcategories.some((sub) => categoryMatchesSearch(sub, phrase));
  };

  const filteredCategories = categorySearch
    ? categoriesData?.items.filter((cat) => categoryMatchesSearch(cat, categorySearch))
    : categoriesData?.items;

  // funkcja renderująca drzewo kategorii
  const renderCategoryTree = useCallback(
    (category: ICategoryListItem) => {
      // funkcja rekurencyjna renderująca  listę wraz z rozwiniętymi podkategoriami
      const renderTree = (categories: ICategoryListItem[] = [], level = 0) =>
        categories.map((category) => (
          <div
            key={category.id}
            className={classnames(styles.category, { [styles.main]: level === 0 })}>
            <div className={styles.row}>
              {category.subcategories_total_count > 0 && (
                <>
                  {expandedCategoryIds.includes(category.id) ||
                  category.subcategories.some((item) => item.id === chosenCategoryId) ? (
                    <DashCircle
                      onClick={() =>
                        setExpandedCategoryIds((prevState) =>
                          prevState.filter((categoryId) => categoryId !== category.id)
                        )
                      }
                    />
                  ) : (
                    <PlusCircle
                      onClick={() =>
                        setExpandedCategoryIds((prevState) => [...prevState, category.id])
                      }
                    />
                  )}
                </>
              )}
              <Link
                to={`/${category.url_link}`}
                className={classnames({ [styles.chosen]: category.id === chosenCategoryId })}>
                {category.name}&nbsp;<span className={styles.counter}>({category.products_count})</span>
              </Link>
            </div>

            {category.subcategories_total_count > 0 &&
              (expandedCategoryIds.includes(category.id) ||
                category.subcategories.some((item) => item.id === chosenCategoryId)) &&
              renderTree(category.subcategories, level + 1)}
          </div>
        ));

      return renderTree([category]);
    },
    [expandedCategoryIds, chosenCategoryId]
  );

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-wise-WiseProducts-components-WiseCategories'
      )}>
      {!categoriesData && <WiseCategoriesSkeleton />}

      {!!categoriesData?.items?.length && (
        <div className={styles.searchWrapper}>
          <input
            className={styles.searchInput}
            type="text"
            placeholder={t('Znajdź kategorię')}
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
          />
          <Search className={styles.searchIcon} />
        </div>
      )}
      {categoriesData && (
        <Grid container>
          {filteredCategories?.length ? (
            filteredCategories.map((category) => (
              <Grid item key={category.id} sm={Math.floor(12 / columnsCount)}>
                {renderCategoryTree(category)}
              </Grid>
            ))
          ) : (
            <div className={styles.info}>
              <ExclamationCircle />
              <span>
                <ReactMarkdown disallowedElements={['p']} unwrapDisallowed>
                  {t('PRODUCTS_SIDEBAR_NO_CATEGORIES_INFO')}
                </ReactMarkdown>
              </span>
            </div>
          )}
        </Grid>
      )}
    </div>
  );
};

export default WiseCategories;
